import './conversations.scss';

import { debounce, kebabCase, lowerCase } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { AgendaAppointmentModal } from '../../compositions/agenda/modals/agenda-appointment-modal/agenda-appointment-modal';
import { ConversationCreateModal } from '../../compositions/conversations/modals/conversation-create-modal/conversation-create-modal';
import {
  ConversationDateRangeContextModal,
  DateRange,
} from '../../compositions/conversations/modals/conversation-date-range-modal/conversation-date-range-modal';
import { ChatMessageSummaryItem } from '../../compositions/conversations/modals/conversation-summary-modal/conversation-summary-modal';
import { useAppContext } from '../../data/context/app-context';
import { useConversationItem } from '../../data/data/conversations/use-conversation-item';
import { useConversationItems } from '../../data/data/conversations/use-conversation-items';
import { useStoredConversationMessageItem } from '../../data/data/conversations/use-stored-conversation-message-item';
import { ChatFilterType } from '../../data/data/enums';
import {
  ConversationCategory,
  ConversationStatus,
} from '../../data/types/api/graphql';
import { DateFormat, formatDate, getAge } from '../../data/utils/date.utils';
import { useFormatters } from '../../data/utils/formatters.utils';
import { useNavigateWithHealthProvider } from '../../data/utils/router.utils';
import {
  ContextMenuHorizontalPosition,
  ContextMenuVerticalPosition,
  useContextMenu,
} from '../../data/utils/use-context-menu';
import { Accordion } from '../../elements/accordion/accordion';
import { Button, ButtonIcon, ButtonType } from '../../elements/button/button';
import { ChatContainer } from '../../elements/chat/chat-container';
import { ChatToolbar } from '../../elements/chat/chat-toolbar';
import { ChatMessageCreateFileModal } from '../../elements/chat/modals/chat-message-file-modal/chat-message-create-file-modal';
import { ScheduleAppointmentModal } from '../../elements/chat/modals/schedule-appointment-modal/schedule-appointment-modal';
import { Content } from '../../elements/content/content';
import { ContentMessage } from '../../elements/content/content-message';
import { ContextMenu } from '../../elements/context-menu/context-menu';
import { BaseInputIcon } from '../../elements/form/input/base-input';
import { InputField } from '../../elements/form/input/input-field';
import { CustomFormattedMessage } from '../../elements/intl/custom-formatted-message';
import { Sidebar } from '../../elements/sidebar/sidebar';
import { SidebarList } from '../../elements/sidebar-list/sidebar-list';
import { SidebarListItem } from '../../elements/sidebar-list/sidebar-list-item';
import { Tabs, TabsSize } from '../../elements/tabs/tabs';
import { RelativeTime } from '../../elements/utils/relative-time/relative-time';
import { useAppointmentModal } from '../../hooks/modal/use-appointment-modal';
import { useScheduleAppointmentModal } from '../../hooks/modal/use-create-chat-default-modal';
import { useCreateChatFileModal } from '../../hooks/modal/use-create-chat-file-modal';

export function ConversationsPage() {
  const intl = useIntl();

  const { healthProviderId, user } = useAppContext();

  // show appointment modal
  const { appointmentModalData, showAppointmentModal } = useAppointmentModal();

  // show create chat default modal
  const { scheduleAppointmentModalData, showScheduleAppointmentModal } =
    useScheduleAppointmentModal();

  // show create chat default modal
  const { createChatFileModalData, showCreateChatFileModal } =
    useCreateChatFileModal();

  const { conversationId } = useParams();

  const [search, setSearch] = useState<string>('');
  const [filter, setFilter] = useState<ChatFilterType>(
    localStorage.getItem('conversationsFilter')
      ? (localStorage.getItem('conversationsFilter') as ChatFilterType)
      : ChatFilterType.ALL
  );

  const [conversationCreateModalOpen, setConversationCreateModalOpen] =
    useState<boolean>(false);

  const onHandleFilter = useCallback((value: ChatFilterType) => {
    localStorage.setItem('conversationsFilter', value);
    setFilter(value);
  }, []);

  const [dateRange, setDateRange] = useState<DateRange | null>(null);

  const hasRange = useMemo(() => {
    if (
      dateRange?.startDate !== undefined ||
      dateRange?.endDate !== undefined
    ) {
      return true;
    }
    return false;
  }, [dateRange]);

  const displayRange = useMemo(() => {
    if (!dateRange) {
      return <CustomFormattedMessage id="conversations.filter.date" />;
    }
    if (dateRange.startDate && !dateRange.endDate) {
      return intl.formatDate(dateRange.startDate);
    }
    if (dateRange.endDate && !dateRange.startDate) {
      return intl.formatDate(dateRange.endDate);
    }
    return (
      <CustomFormattedMessage
        id="common.date-range"
        values={{
          start: intl.formatDate(dateRange.startDate),
          end: intl.formatDate(dateRange.endDate),
        }}
      />
    );
  }, [dateRange, intl]);

  const onHandleDateRange = useCallback((range: DateRange | null) => {
    setDateRange(range);
  }, []);

  const { data: conversationItemsData, loading: conversationItemsLoading } =
    useConversationItems(
      {
        healthProviderId,
        healthProfessionalId:
          filter === ChatFilterType.MINE ? user?.id : undefined,
        search,
        startDate: dateRange?.startDate
          ? formatDate(dateRange.startDate, DateFormat)
          : null,

        endDate: dateRange?.endDate
          ? formatDate(dateRange.endDate, DateFormat)
          : null,
      },
      dateRange?.endDate !== undefined ||
        dateRange?.startDate !== undefined ||
        search.length > 2 // disable realtime updates
    );

  const { data: conversationItemData } = useConversationItem(conversationId);

  const [category, setCategory] = useState<ConversationCategory>(
    ConversationCategory.Chat
  );

  // auto select tab
  useEffect(() => {
    if (conversationItemData) {
      setCategory(conversationItemData.category);
    }
  }, [conversationItemData]);

  const filteredConversationItemsData = useMemo(() => {
    if (!conversationItemsData) {
      return [];
    }
    return conversationItemsData.filter(item => item.category === category);
  }, [category, conversationItemsData]);

  const bulkCount = useMemo(() => {
    if (!conversationItemsData) {
      return 0;
    }
    return conversationItemsData.filter(
      item => item.category === ConversationCategory.Bulk && item.noReply
    ).length;
  }, [conversationItemsData]);

  const medicationRequestCount = useMemo(() => {
    if (!conversationItemsData) {
      return 0;
    }
    return conversationItemsData.filter(
      item =>
        item.category === ConversationCategory.MedicationRequest && item.noReply
    ).length;
  }, [conversationItemsData]);

  const [selectedChatMessageItems, setSelectedChatMessageItems] = useState<
    ChatMessageSummaryItem[]
  >([]);

  const navigate = useNavigateWithHealthProvider();

  const onHandleConversation = useCallback(
    (id: string) => {
      navigate(`/conversations/${id}`);
    },
    [navigate]
  );

  const onHandleSearch = useCallback((value: string) => {
    setSearch(value);
  }, []);

  const onHandleSearchDebounce = useMemo(
    () => debounce(onHandleSearch, 250),
    [onHandleSearch]
  );

  const allStatuses = [
    ConversationStatus.Active,
    ConversationStatus.Monitored,
    ConversationStatus.Reminder,
    ConversationStatus.OnHold,
    ConversationStatus.Finished,
  ];

  const statuses =
    category === ConversationCategory.Bulk ||
    category === ConversationCategory.MedicationRequest
      ? allStatuses.filter(status => status !== ConversationStatus.Reminder)
      : allStatuses;

  const {
    contextMenuState: filterContextMenuState,
    onOpenContextMenu: onOpenFilterContextMenu,
  } = useContextMenu({
    center: false,
    vertical: ContextMenuVerticalPosition.Bottom,
  });

  const {
    contextMenuState: rangeFilterContextMenuState,
    onOpenContextMenu: onOpenRangeFilterContextMenu,
    onCloseContextMenu: onCloseRangeFilterContextMenu,
  } = useContextMenu({
    center: false,
    horizontal: ContextMenuHorizontalPosition.Right,
    vertical: ContextMenuVerticalPosition.Bottom,
  });

  const { getFullName } = useFormatters();

  const { load } = useStoredConversationMessageItem();

  return (
    <div className="conversations">
      <Sidebar
        size="large"
        title={<CustomFormattedMessage id="conversations.title" />}
        toolbar={
          <>
            <InputField
              icon={BaseInputIcon.SEARCH}
              type="search"
              name="search"
              placeholder={intl.formatMessage({
                id: 'common.patients-search-placeholders',
              })}
              onChange={value => onHandleSearchDebounce(value)}
            />

            <div className="filter">
              <Button
                testId="chat-filter"
                type={
                  filter === ChatFilterType.MINE
                    ? ButtonType.WARNING
                    : ButtonType.SECONDARY
                }
                toggle
                onClick={onOpenFilterContextMenu}
              >
                <CustomFormattedMessage
                  id={`conversations.filter.${filter.toLowerCase()}`}
                />
              </Button>

              <Button
                testId="range-filter"
                type={hasRange ? ButtonType.WARNING : ButtonType.SECONDARY}
                onClick={onOpenRangeFilterContextMenu}
              >
                {displayRange}
              </Button>
            </div>

            <Tabs<ConversationCategory>
              value={category}
              size={TabsSize.FULL}
              items={[
                {
                  key: ConversationCategory.Chat,
                  title: (
                    <CustomFormattedMessage
                      id={`conversations.filter.${kebabCase(
                        ConversationCategory.Chat
                      )}`}
                    />
                  ),
                },
                {
                  key: ConversationCategory.MedicationRequest,
                  title: (
                    <>
                      <CustomFormattedMessage
                        id={`conversations.filter.${kebabCase(
                          ConversationCategory.MedicationRequest
                        )}`}
                      />
                      {category !== ConversationCategory.MedicationRequest &&
                      medicationRequestCount ? (
                        <i className="icon bell" />
                      ) : undefined}
                    </>
                  ),
                },
                {
                  key: ConversationCategory.Bulk,
                  title: (
                    <>
                      <CustomFormattedMessage
                        id={`conversations.filter.${kebabCase(
                          ConversationCategory.Bulk
                        )}`}
                      />
                      {category !== ConversationCategory.Bulk && bulkCount ? (
                        <i className="icon bell" />
                      ) : undefined}
                    </>
                  ),
                },
              ]}
              onChange={value => {
                setCategory(value);
                navigate('/conversations', { replace: true }); // clear id when changing category
              }}
            />
          </>
        }
        action={
          <Button
            testId="chat-create"
            icon={ButtonIcon.CHAT_ADD}
            onClick={() => setConversationCreateModalOpen(true)}
          >
            <CustomFormattedMessage id="conversations.button.create-conversation" />
          </Button>
        }
      >
        <Accordion
          items={statuses.map(status => {
            const conversationItems = filteredConversationItemsData.filter(
              option => {
                if (status === ConversationStatus.Active) {
                  return [
                    ConversationStatus.Active,
                    ConversationStatus.WaitList,
                  ].includes(option.status as unknown as ConversationStatus);
                }

                return (
                  (option.status as unknown as ConversationStatus) === status
                );
              }
            );

            return {
              key: status,
              title: (
                <CustomFormattedMessage
                  id={`common.conversation.${
                    status === ConversationStatus.Active
                      ? 'open'
                      : kebabCase(status)
                  }`}
                />
              ),
              count: conversationItems.length,
              content: (
                <SidebarList
                  loading={conversationItemsLoading}
                  items={conversationItems.map(option => (
                    <SidebarListItem
                      key={option.id}
                      type={kebabCase(option.status)}
                      title={
                        <>
                          {option.patient ? (
                            getFullName(option.patient)
                          ) : (
                            <CustomFormattedMessage id="common.unknown-patient" />
                          )}
                          <small>
                            {`${intl
                              .formatMessage({
                                id: `common.gender.${lowerCase(
                                  option.patient.gender
                                )}`,
                              })
                              .charAt(0)} (${intl.formatMessage(
                              {
                                id: `common.age`,
                              },
                              { age: getAge(option.patient?.birthDate) }
                            )})`}
                          </small>
                        </>
                      }
                      values={{
                        ...(option.patient
                          ? {
                              time: (
                                <>
                                  <RelativeTime value={option.lastUpdated} />
                                  {load(option.id) && (
                                    <span className="concept">
                                      <CustomFormattedMessage id="common.concept" />
                                    </span>
                                  )}
                                </>
                              ),
                              state: (
                                <>
                                  <i
                                    className={`icon euro ${
                                      !option.patient.dutchInsurance
                                        ? 'active'
                                        : ''
                                    }`}
                                  />
                                  <i
                                    className={`icon bell ${
                                      option.isNew ? 'active' : ''
                                    }`}
                                  />
                                </>
                              ),
                            }
                          : {}),
                      }}
                      avatar={option.healthProfessional?.initials ?? true}
                      update={option.noReply}
                      active={conversationId === option.id}
                      urgent={option.urgent}
                      onClick={() => onHandleConversation(option.id)}
                    />
                  ))}
                />
              ),
            };
          })}
        />
      </Sidebar>

      <Content
        toolbar={
          conversationId ? (
            <ChatToolbar
              healthProviderId={healthProviderId}
              conversationItem={conversationItemData}
              selectedChatMessageItems={selectedChatMessageItems}
            />
          ) : undefined
        }
      >
        {!conversationId ? (
          <ContentMessage
            message={
              <CustomFormattedMessage id="conversations.select-conversation" />
            }
          />
        ) : (
          <ChatContainer
            key={conversationId}
            showCreateChatDefaultModal={showScheduleAppointmentModal}
            showAppointmentModal={showAppointmentModal}
            showCreateChatFileModal={showCreateChatFileModal}
            healthProviderId={healthProviderId}
            conversationItem={conversationItemData}
            onSelectMessages={setSelectedChatMessageItems}
          />
        )}
      </Content>

      {conversationCreateModalOpen && (
        <ConversationCreateModal
          healthProviderId={healthProviderId}
          onSaved={onHandleConversation}
          onClose={() => {
            setConversationCreateModalOpen(false);
          }}
        />
      )}

      {appointmentModalData.value && (
        <AgendaAppointmentModal {...appointmentModalData.value} />
      )}

      {scheduleAppointmentModalData && (
        <ScheduleAppointmentModal {...scheduleAppointmentModalData} />
      )}

      {createChatFileModalData && (
        <ChatMessageCreateFileModal {...createChatFileModalData} />
      )}
      {rangeFilterContextMenuState && (
        <ConversationDateRangeContextModal
          state={rangeFilterContextMenuState}
          selected={dateRange}
          onSaved={onHandleDateRange}
          onClose={onCloseRangeFilterContextMenu}
        />
      )}

      {filterContextMenuState && (
        <ContextMenu
          testId="chat-filter"
          state={filterContextMenuState}
          items={[
            {
              key: 'all',
              title: <CustomFormattedMessage id="conversations.filter.all" />,
              onClick: () => onHandleFilter(ChatFilterType.ALL),
            },
            {
              key: 'mine',
              title: <CustomFormattedMessage id="conversations.filter.mine" />,
              onClick: () => onHandleFilter(ChatFilterType.MINE),
            },
          ]}
        />
      )}
    </div>
  );
}
